import React from 'react';
import { View, TouchableOpacity, StyleSheet, GestureResponderEvent, Image, Linking } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { Quote } from '../../types/work';
import { getRelativeTime } from '../../data/mockWorkData';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

const LIGHT_BORDER_GRADIENT: readonly [string, string, string] = [
  'rgba(255,255,255,0.85)',
  'rgba(185,185,185,0.55)',
  'rgba(245,245,245,0.8)',
];

const DARK_BORDER_GRADIENT: readonly [string, string, string] = [
  'rgba(255,255,255,0.55)',
  'rgba(120,120,120,0.35)',
  'rgba(235,235,235,0.45)',
];

const STATUS_META: Record<Quote['status'], {
  label: string;
  dot: string;
  lightBg: string;
  darkBg: string;
  lightBorder: string;
  darkBorder: string;
  lightText: string;
  darkText: string;
}> = {
  sent: {
    label: 'Sent',
    dot: '#F5A524',
    lightBg: 'rgba(245,165,36,0.18)',
    darkBg: 'rgba(245,165,36,0.12)',
    lightBorder: 'rgba(245,165,36,0.4)',
    darkBorder: 'rgba(245,165,36,0.28)',
    lightText: '#B86B09',
    darkText: '#FFD8A1',
  },
  viewed: {
    label: 'Viewed',
    dot: '#0A66C2',
    lightBg: 'rgba(10,102,194,0.18)',
    darkBg: 'rgba(10,102,194,0.15)',
    lightBorder: 'rgba(10,102,194,0.35)',
    darkBorder: 'rgba(10,102,194,0.25)',
    lightText: '#0A4B8E',
    darkText: '#90C7FF',
  },
  accepted: {
    label: 'Accepted',
    dot: '#11A45E',
    lightBg: 'rgba(17,164,94,0.18)',
    darkBg: 'rgba(17,164,94,0.15)',
    lightBorder: 'rgba(17,164,94,0.35)',
    darkBorder: 'rgba(17,164,94,0.25)',
    lightText: '#0C6D3F',
    darkText: '#7BE2AD',
  },
  completion_requested: {
    label: 'Pending',
    dot: '#FF8C00',
    lightBg: 'rgba(255,140,0,0.18)',
    darkBg: 'rgba(255,140,0,0.15)',
    lightBorder: 'rgba(255,140,0,0.35)',
    darkBorder: 'rgba(255,140,0,0.25)',
    lightText: '#CC7000',
    darkText: '#FFB84D',
  },
  completed: {
    label: 'Ended - Completed',
    dot: '#10B981',
    lightBg: 'rgba(16,185,129,0.18)',
    darkBg: 'rgba(16,185,129,0.15)',
    lightBorder: 'rgba(16,185,129,0.35)',
    darkBorder: 'rgba(16,185,129,0.25)',
    lightText: '#059669',
    darkText: '#6EE7B7',
  },
  partially_completed: {
    label: 'Ended - Partial',
    dot: '#F59E0B',
    lightBg: 'rgba(245,158,11,0.18)',
    darkBg: 'rgba(245,158,11,0.15)',
    lightBorder: 'rgba(245,158,11,0.35)',
    darkBorder: 'rgba(245,158,11,0.25)',
    lightText: '#D97706',
    darkText: '#FCD34D',
  },
  cancelled: {
    label: 'Ended - Cancelled',
    dot: '#EF4444',
    lightBg: 'rgba(239,68,68,0.18)',
    darkBg: 'rgba(239,68,68,0.15)',
    lightBorder: 'rgba(239,68,68,0.35)',
    darkBorder: 'rgba(239,68,68,0.25)',
    lightText: '#DC2626',
    darkText: '#FCA5A5',
  },
};

const getStatusAppearance = (status: Quote['status'], isDark: boolean) => {
  const meta = STATUS_META[status] || STATUS_META.sent;
  return {
    label: meta.label,
    dot: meta.dot,
    background: isDark ? meta.darkBg : meta.lightBg,
    border: isDark ? meta.darkBorder : meta.lightBorder,
    text: isDark ? meta.darkText : meta.lightText,
  };
};

const formatFileSize = (bytes?: number | null) => {
  if (!bytes || bytes <= 0) {
    return 'PDF';
  }

  const kb = bytes / 1024;
  if (kb >= 1024) {
    return `${(kb / 1024).toFixed(1)} MB`;
  }

  return `${Math.max(kb, 0.1).toFixed(1)} KB`;
};

interface QuoteCardProps {
  quote: Quote;
  jobTitle: string;
  clientName?: string;
  currentUserId?: string; // To determine if user can confirm completion
  onPress: () => void;
  showFreelancerInfo?: boolean;
  onProfilePress?: () => void;
  onChatPress?: () => void;
  showChatButton?: boolean;
  showAcceptButton?: boolean;
  onAcceptPress?: () => void;
  showEditButton?: boolean;
  onEditPress?: () => void;
  showRequestCompletionButton?: boolean;
  onRequestCompletionPress?: () => void;
  showConfirmCompletionButton?: boolean;
  onConfirmCompletionPress?: () => void;
  showEndContractButton?: boolean;
  onEndContractPress?: () => void;
  showRespondEndContractButton?: boolean;
  onRespondEndContractPress?: () => void;
  pendingCompletionRequest?: {
    id: string;
    requestedStatus: 'completed' | 'partially_completed' | 'cancelled';
    requestedById: string;
    status: 'pending' | 'accepted' | 'rejected';
  };
}

const QuoteCard: React.FC<QuoteCardProps> = ({
  quote,
  jobTitle,
  clientName,
  currentUserId,
  onPress,
  showFreelancerInfo = false,
  onProfilePress,
  onChatPress,
  showChatButton = false,
  showAcceptButton = false,
  onAcceptPress,
  showEditButton = false,
  onEditPress,
  showRequestCompletionButton = false,
  onRequestCompletionPress,
  showConfirmCompletionButton = false,
  onConfirmCompletionPress,
  showEndContractButton = false,
  onEndContractPress,
  showRespondEndContractButton = false,
  onRespondEndContractPress,
  pendingCompletionRequest,
}) => {
  const { theme, isDark } = useTheme();
  const canShowChat = showChatButton && typeof onChatPress === 'function';
  const canShowAccept = showAcceptButton && typeof onAcceptPress === 'function' && quote.status === 'sent';
  const canShowEdit = showEditButton && typeof onEditPress === 'function' && quote.status === 'sent';
  const canShowRequestCompletion = showRequestCompletionButton && typeof onRequestCompletionPress === 'function' && quote.status === 'accepted';
  const canShowConfirmCompletion = showConfirmCompletionButton && typeof onConfirmCompletionPress === 'function' && quote.status === 'completion_requested' && quote.completionRequestedBy !== currentUserId;
  const canShowEndContract = showEndContractButton && typeof onEndContractPress === 'function' && quote.status === 'accepted';
  const canShowRespondEndContract = showRespondEndContractButton && typeof onRespondEndContractPress === 'function';
  const gradientColors = isDark ? DARK_BORDER_GRADIENT : LIGHT_BORDER_GRADIENT;
  const statusAppearance = getStatusAppearance(quote.status, isDark);
  const displayName = showFreelancerInfo
    ? quote.freelancer?.name || 'Unknown Freelancer'
    : clientName || 'Unknown Client';
  const avatarLetter = displayName.charAt(0).toUpperCase();
  const avatarBackground = `${theme.accent}22`;

  const handleChatPress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    onChatPress?.();
  };

  const handleProfilePress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    onProfilePress?.();
  };

  const handleAcceptPress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    onAcceptPress?.();
  };

  const handleEditPress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    onEditPress?.();
  };

  const handleRequestCompletionPress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    onRequestCompletionPress?.();
  };

  const handleConfirmCompletionPress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    onConfirmCompletionPress?.();
  };

  const handleEndContractPress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    onEndContractPress?.();
  };

  const handleRespondEndContractPress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    onRespondEndContractPress?.();
  };

  const handleAttachmentPress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    if (quote.attachmentUrl) {
      Linking.openURL(quote.attachmentUrl).catch(error => {
        console.error('Failed to open quote attachment', error);
      });
    }
  };
  
  return (
    <TouchableOpacity
      style={[styles.cardWrapper, { shadowColor: theme.shadow }]}
      onPress={onPress}
      activeOpacity={0.85}
    >
      <LinearGradient
        colors={gradientColors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.cardBorder}
      >
        <View
          style={[
            styles.cardInner,
            {
              backgroundColor: theme.card,
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
            },
          ]}
        >
          <View style={styles.headerRow}>
            {showFreelancerInfo && (
              <TouchableOpacity 
                style={styles.personBlock}
                onPress={handleProfilePress}
                activeOpacity={0.7}
              >
                {quote.freelancer?.photo ? (
                  <Image source={{ uri: quote.freelancer.photo }} style={styles.avatar} />
                ) : (
                  <View style={[styles.avatarPlaceholder, { backgroundColor: avatarBackground }]}>
                    <ThemedText style={[styles.avatarLetter, { color: theme.accent }]}>
                      {avatarLetter}
                    </ThemedText>
                  </View>
                )}
                <View style={styles.personText}>
                  <ThemedText style={[styles.name, { color: theme.text }]} numberOfLines={1}>
                    {displayName}
                  </ThemedText>
                  {quote.freelancer?.profession && (
                    <ThemedText style={[styles.profession, { color: theme.textSecondary }]} numberOfLines={1}>
                      {quote.freelancer.profession}
                    </ThemedText>
                  )}
                </View>
              </TouchableOpacity>
            )}
            {!showFreelancerInfo && (
              <View style={styles.jobMetaBlock}>
                {quote.job?.type && (
                  <View style={[styles.typeBadge, { 
                    backgroundColor: quote.job.type === 'contract' ? theme.primary + '20' : theme.success + '20',
                    marginRight: 6,
                  }]}>
                    <ThemedText style={[styles.typeText, { 
                      color: quote.job.type === 'contract' ? theme.primary : theme.success 
                    }]}>
                      {quote.job.type === 'contract' ? 'Contract' : quote.job.type === 'supply' ? 'Supply' : 'Product'}
                    </ThemedText>
                  </View>
                )}
                <Ionicons name="briefcase-outline" size={12} color={theme.textSecondary} />
                <ThemedText style={[styles.jobTitle, { color: theme.textSecondary }]} numberOfLines={1}>
                  {jobTitle}
                </ThemedText>
              </View>
            )}
            <View
              style={[
                styles.statusBadge,
                {
                  backgroundColor: statusAppearance.background,
                  borderColor: statusAppearance.border,
                },
              ]}
            >
              <View style={[styles.statusDot, { backgroundColor: statusAppearance.dot }]} />
              <ThemedText style={[styles.statusBadgeText, { color: statusAppearance.text }]}>
                {statusAppearance.label}
              </ThemedText>
            </View>
          </View>

          <View
            style={[styles.amountBlock, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
          >
            <View>
              <ThemedText style={[styles.amountLabel, { color: theme.textSecondary }]}>Quoted Amount</ThemedText>
              <ThemedText style={[styles.amountValue, { color: theme.accent }]}>
                ${quote.amount.toLocaleString('en-IN')}
              </ThemedText>
            </View>
            <View
              style={[
                styles.timeChip,
                {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                },
              ]}
            >
              <Ionicons name="time-outline" size={14} color={theme.textSecondary} />
              <ThemedText style={[styles.timeText, { color: theme.textSecondary }]}>
                {getRelativeTime(quote.createdAt)}
              </ThemedText>
            </View>
          </View>

          {quote.message && (
            <View
              style={[
                styles.messageBubble,
                { backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', borderColor: theme.border },
              ]}
            >
              <Ionicons
                name="chatbubble-ellipses-outline"
                size={16}
                color={theme.textSecondary}
                style={styles.messageIcon}
              />
              <ThemedText style={[styles.messageText, { color: theme.textSecondary }]} numberOfLines={3}>
                {quote.message}
              </ThemedText>
            </View>
          )}

          {quote.attachmentUrl && (
            <TouchableOpacity
              style={[
                styles.attachmentCard,
                {
                  borderColor: theme.border,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                },
              ]}
              onPress={handleAttachmentPress}
              activeOpacity={0.85}
            >
              <View style={[styles.attachmentIcon, { backgroundColor: theme.accent + '22' }]}>
                <Ionicons name="document-text-outline" size={20} color={theme.accent} />
              </View>
              <View style={styles.attachmentInfo}>
                <ThemedText style={[styles.attachmentName, { color: theme.text }]} numberOfLines={2}>
                  {quote.attachmentName || 'Quotation.pdf'}
                </ThemedText>
                <ThemedText style={[styles.attachmentMeta, { color: theme.textSecondary }]}>
                  {formatFileSize(quote.attachmentSize)}
                </ThemedText>
              </View>
              <Ionicons name="open-outline" size={20} color={theme.accent} />
            </TouchableOpacity>
          )}

          {pendingCompletionRequest && pendingCompletionRequest.status === 'pending' && (
            <View
              style={[
                styles.pendingRequestBanner,
                {
                  backgroundColor: isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
                  borderColor: '#F59E0B',
                },
              ]}
            >
              <View style={styles.pendingRequestContent}>
                <Ionicons name="time-outline" size={20} color="#F59E0B" />
                <View style={styles.pendingRequestText}>
                  <ThemedText style={[styles.pendingRequestTitle, { color: theme.text }]}>
                    {pendingCompletionRequest.requestedById === currentUserId
                      ? 'Waiting for approval'
                      : 'Action required'}
                  </ThemedText>
                  <ThemedText style={[styles.pendingRequestSubtitle, { color: theme.textSecondary }]}>
                    {pendingCompletionRequest.requestedById === currentUserId
                      ? `End contract request sent (${pendingCompletionRequest.requestedStatus.replace('_', ' ')})`
                      : 'Please respond to the end contract request'}
                  </ThemedText>
                </View>
              </View>
            </View>
          )}

          {(canShowChat || canShowAccept || canShowEdit || canShowRequestCompletion || canShowConfirmCompletion || canShowEndContract || canShowRespondEndContract) && (
            <View style={styles.actionsRow}>
              {canShowChat && (
                <TouchableOpacity
                  style={[styles.secondaryButton, { borderColor: theme.border }]}
                  onPress={handleChatPress}
                  activeOpacity={0.85}
                >
                  <Ionicons name="chatbubble-outline" size={16} color={theme.textSecondary} />
                  <ThemedText style={[styles.secondaryButtonText, { color: theme.text }]}>Chat</ThemedText>
                </TouchableOpacity>
              )}
              {canShowEdit && (
                <TouchableOpacity
                  style={[styles.secondaryButton, { borderColor: theme.border }]}
                  onPress={handleEditPress}
                  activeOpacity={0.85}
                >
                  <Ionicons name="create-outline" size={16} color={theme.textSecondary} />
                  <ThemedText style={[styles.secondaryButtonText, { color: theme.text }]}>Edit</ThemedText>
                </TouchableOpacity>
              )}
              {canShowEndContract && (
                <TouchableOpacity
                  style={[styles.primaryButton, { backgroundColor: theme.accent }]}
                  onPress={handleEndContractPress}
                  activeOpacity={0.9}
                >
                  <Ionicons name="flag-outline" size={18} color="#FFFFFF" />
                  <ThemedText style={styles.primaryButtonText}>End Contract</ThemedText>
                </TouchableOpacity>
              )}
              {canShowRespondEndContract && (
                <TouchableOpacity
                  style={[styles.primaryButton, { backgroundColor: '#F59E0B' }]}
                  onPress={handleRespondEndContractPress}
                  activeOpacity={0.9}
                >
                  <Ionicons name="alert-circle" size={18} color="#FFFFFF" />
                  <ThemedText style={styles.primaryButtonText}>Respond to Request</ThemedText>
                </TouchableOpacity>
              )}
              {canShowRequestCompletion && (
                <TouchableOpacity
                  style={[styles.primaryButton, { backgroundColor: theme.accent }]}
                  onPress={handleRequestCompletionPress}
                  activeOpacity={0.9}
                >
                  <Ionicons name="flag-outline" size={18} color="#FFFFFF" />
                  <ThemedText style={styles.primaryButtonText}>Request Completion</ThemedText>
                </TouchableOpacity>
              )}
              {canShowConfirmCompletion && (
                <TouchableOpacity
                  style={[styles.primaryButton, { backgroundColor: theme.success }]}
                  onPress={handleConfirmCompletionPress}
                  activeOpacity={0.9}
                >
                  <Ionicons name="checkmark-done-circle" size={18} color="#FFFFFF" />
                  <ThemedText style={styles.primaryButtonText}>Confirm Completion</ThemedText>
                </TouchableOpacity>
              )}
              {canShowAccept && (
                <TouchableOpacity
                  style={[styles.primaryButton, { backgroundColor: theme.success }]}
                  onPress={handleAcceptPress}
                  activeOpacity={0.9}
                >
                  <Ionicons name="checkmark-circle" size={18} color="#FFFFFF" />
                  <ThemedText style={styles.primaryButtonText}>Accept Quote</ThemedText>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>
      </LinearGradient>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  cardWrapper: {
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 4,
  },
  cardBorder: {
    borderRadius: 20,
    padding: 1,
  },
  cardInner: {
    borderRadius: 18,
    borderWidth: 1,
    padding: 20,
    gap: 16,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  personBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  avatarPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarLetter: {
    fontSize: 20,
    fontWeight: '700',
  },
  personText: {
    flex: 1,
  },
  name: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  profession: {
    fontSize: 13,
    marginBottom: 2,
  },
  jobMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  jobMetaBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  typeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  typeText: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  jobTitle: {
    fontSize: 13,
    flex: 1,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    gap: 6,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  amountBlock: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 16,
    borderWidth: 1,
    paddingHorizontal: 18,
    paddingVertical: 14,
    gap: 16,
  },
  amountLabel: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 6,
  },
  amountValue: {
    fontSize: 24,
    fontWeight: '800',
    letterSpacing: -0.2,
  },
  timeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    gap: 6,
  },
  timeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  messageBubble: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderRadius: 14,
    borderWidth: 1,
    padding: 14,
    gap: 10,
  },
  messageIcon: {
    marginTop: 2,
  },
  messageText: {
    fontSize: 14,
    lineHeight: 20,
    flex: 1,
  },
  attachmentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 12,
  },
  attachmentIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  attachmentInfo: {
    flex: 1,
  },
  attachmentName: {
    fontSize: 14,
    fontWeight: '600',
  },
  attachmentMeta: {
    fontSize: 12,
    marginTop: 2,
  },
  pendingRequestBanner: {
    borderWidth: 1.5,
    borderRadius: 12,
    padding: 14,
  },
  pendingRequestContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pendingRequestText: {
    flex: 1,
  },
  pendingRequestTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 2,
  },
  pendingRequestSubtitle: {
    fontSize: 12,
    lineHeight: 16,
  },
  actionsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
    gap: 6,
  },
  secondaryButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    paddingHorizontal: 18,
    paddingVertical: 12,
    gap: 8,
  },
  primaryButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});

export default QuoteCard;
